/* eslint-disable @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import {
  alpha,
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputBase,
} from '@mui/material';
import { RawGreeksDataMap, RawStatsData } from 'types';
import EditableUnderlyingPosition from './EditableUnderlyingPosition';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import {
  PositionLeg,
  IVAdjustmentSettings,
  PositionShares,
  NEW_POSITION_ID,
  Position as PositionType,
  OC_DEFAULT_CALL_SLOPE_MULT,
  OC_DEFAULT_LEVEL_SHIFT,
  OC_DEFAULT_PUT_SLOPE_MULT,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import {
  generateDefaultOptionLeg,
  getOptionLegPrice,
  getPositionDeletes,
  getUpdatedLeg,
} from 'util/optionsCalculator';
import GroupedOptionLegs from './GroupedOptionLegs';
import useToast from 'hooks/useToast';
import { dayjs, formatAsCurrency, formatAsPercentage } from 'util/shared';
import { useRecoilValue } from 'recoil';
import {
  negativeTrendColorState,
  positiveTrendColorState,
  userIsLoggedInState,
} from 'states';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { SGTooltip } from 'components/core';

interface PositionProps {
  position: PositionType;
  getRiskFreeRate: (tte: number) => number;
  legIVs: Map<number, number>; // leg id to iv
  adjustedLegIVs?: Map<number, number>; // leg id to adjusted iv
  originalPosition?: PositionType; // For saved positions, to track changes
  onSetPosition: (position: PositionType | Partial<PositionType>) => void;
  sym: string;
  isActive: boolean;
  greeks?: RawGreeksDataMap;
  greeksLoading?: boolean;
  greekStats?: RawStatsData;
  strikeDataPerExpiration: Map<number, StrikeDeltaObject[]>;
  currentPrice?: number;
  onSetActivePositionId: (positionId: number) => void;
  savePosition?: (
    position: PositionType,
    sym: string,
    name: string,
  ) => Promise<PositionType | null>;
  editSavedPosition?: (
    position: Partial<PositionType>,
    sym: string,
  ) => Promise<void>;
  deleteSavedPosition?: (positionId: number) => Promise<void>;
  hasUnsavedChanges?: boolean;
  isCollapsible?: boolean;
  defaultExpanded?: boolean;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
}

const Position = ({
  position,
  originalPosition,
  getRiskFreeRate,
  legIVs,
  adjustedLegIVs,
  onSetPosition,
  sym,
  isActive,
  greeks,
  greeksLoading = false,
  greekStats,
  strikeDataPerExpiration,
  currentPrice,
  onSetActivePositionId,
  savePosition,
  editSavedPosition,
  deleteSavedPosition,
  hasUnsavedChanges = false,
  isCollapsible = false,
  defaultExpanded = true,
  setForceDefaultZoom,
}: PositionProps) => {
  const theme = useTheme();
  const userIsLoggedIn = useRecoilValue(userIsLoggedInState);
  const { openToast } = useToast();
  const { id: positionId } = position;

  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [clearDialogOpen, setClearDialogOpen] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);
  const [isActionLoading, setIsActionLoading] = useState<boolean>(false);
  const [positionName, setPositionName] = useState<string>(position.name ?? '');
  const [editingName, setEditingName] = useState<boolean>(false);

  // Debounce refs for name updates
  const nameUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingNameRef = useRef<string>(position.name ?? '');

  const positiveColor = useRecoilValue(positiveTrendColorState);
  const negativeColor = useRecoilValue(negativeTrendColorState);

  // Check if this is a saved position
  const isSavedPosition = positionId !== NEW_POSITION_ID;

  // Update position helper
  const updatePosition = useCallback(
    (updates: Partial<PositionType>) => {
      const updatedPosition = { ...position, ...updates };
      onSetPosition(updatedPosition);
    },
    [position, onSetPosition],
  );

  // Debounced name update for saved positions
  const debouncedUpdatePositionName = useCallback(
    (newName: string) => {
      if (nameUpdateTimeoutRef.current) {
        clearTimeout(nameUpdateTimeoutRef.current);
      }

      pendingNameRef.current = newName;

      nameUpdateTimeoutRef.current = setTimeout(() => {
        updatePosition({ name: pendingNameRef.current });
        nameUpdateTimeoutRef.current = null;
      }, 500); // 500ms debounce delay
    },
    [updatePosition],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (nameUpdateTimeoutRef.current) {
        clearTimeout(nameUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Sync position name when position changes from external source
  useEffect(() => {
    if (position.name !== positionName && !editingName) {
      setPositionName(position.name ?? '');
      pendingNameRef.current = position.name ?? '';
    }
  }, [position.name, positionName, editingName]);

  const handleClearPosition = useCallback(() => {
    updatePosition({
      option_legs: [],
      underlying_shares: [],
      iv_adjustments: [],
    });
  }, [updatePosition]);

  const handleAddLeg = useCallback(() => {
    const defaultLeg = generateDefaultOptionLeg(
      sym,
      getRiskFreeRate,
      greeks,
      currentPrice,
    );
    if (defaultLeg) {
      updatePosition({
        option_legs: [
          ...(position.option_legs ?? []),
          {
            ...defaultLeg,
            position_id: positionId,
          },
        ],
      });
    }
  }, [
    updatePosition,
    position.option_legs,
    sym,
    getRiskFreeRate,
    greeks,
    currentPrice,
    positionId,
  ]);

  const handleAddCallLeg = useCallback(() => {
    const defaultLeg = generateDefaultOptionLeg(
      sym,
      getRiskFreeRate,
      greeks,
      currentPrice,
    );
    if (defaultLeg) {
      updatePosition({
        option_legs: [
          ...(position.option_legs ?? []),
          {
            ...defaultLeg,
            option_type: 'C',
            position_id: positionId,
          },
        ],
      });
    }
  }, [
    updatePosition,
    position.option_legs,
    sym,
    greeks,
    currentPrice,
    positionId,
    getRiskFreeRate,
  ]);

  const handleAddPutLeg = useCallback(() => {
    const defaultLeg = generateDefaultOptionLeg(
      sym,
      getRiskFreeRate,
      greeks,
      currentPrice,
    );
    if (defaultLeg) {
      updatePosition({
        option_legs: [
          ...(position.option_legs ?? []),
          {
            ...defaultLeg,
            option_type: 'P',
            position_id: positionId,
          },
        ],
      });
    }
  }, [
    updatePosition,
    position.option_legs,
    sym,
    greeks,
    currentPrice,
    positionId,
    getRiskFreeRate,
  ]);

  const handleAddShares = useCallback(() => {
    const defaultShares: PositionShares = {
      position_id: positionId,
      quantity: 100,
      price_paid: currentPrice!,
      sym,
    };
    updatePosition({
      underlying_shares: [...(position.underlying_shares ?? []), defaultShares],
    });
  }, [
    updatePosition,
    position.underlying_shares,
    currentPrice,
    sym,
    positionId,
  ]);

  const handleEditShares = useCallback(
    (index: number, updatedFields: Partial<PositionShares>) => {
      const updatedShares = [...(position.underlying_shares ?? [])];
      updatedShares[index] = { ...updatedShares[index], ...updatedFields };
      updatePosition({ underlying_shares: updatedShares });
    },
    [updatePosition, position.underlying_shares],
  );

  const handleDeleteShares = useCallback(
    (index: number) => {
      const updatedShares = position.underlying_shares?.filter(
        (_, i) => i !== index,
      );
      updatePosition({ underlying_shares: updatedShares ?? [] });
    },
    [updatePosition, position.underlying_shares],
  );

  const handleGroupExpirationChange = useCallback(
    (newExpiration: number, legIds: number[]) => {
      const updatedLegs =
        position.option_legs?.map((leg) => {
          if (legIds.includes(leg.leg_id)) {
            const defaultPrice = getOptionLegPrice(
              newExpiration,
              leg.strike,
              leg.option_type,
              greeks,
            );
            return {
              ...leg,
              expiry: newExpiration,
              price_paid:
                defaultPrice != null
                  ? Math.round(defaultPrice * 1_000) / 1_000
                  : leg.price_paid,
            };
          }
          return leg;
        }) ?? [];
      updatePosition({ option_legs: updatedLegs });
    },
    [updatePosition, position.option_legs, greeks],
  );

  const handleUpdateLeg = useCallback(
    (
      legId: number,
      updatedLeg: Partial<PositionLeg>,
      shouldUseDefaultPrice = false,
    ) => {
      if (greeks == null) {
        return;
      }
      const updatedLegs = position.option_legs?.map((leg) =>
        leg.leg_id === legId
          ? getUpdatedLeg(leg, updatedLeg, shouldUseDefaultPrice, greeks)
          : leg,
      );
      updatePosition({ option_legs: updatedLegs ?? [] });
    },
    [updatePosition, position.option_legs, greeks],
  );

  const removeLeg = useCallback(
    (legId: number) => {
      const updatedLegs = position.option_legs?.filter(
        (leg) => leg.leg_id !== legId,
      );
      updatePosition({ option_legs: updatedLegs ?? [] });
    },
    [updatePosition, position.option_legs],
  );

  const removeLegsBulk = useCallback(
    (legIds: number[]) => {
      const updatedLegs = position.option_legs?.filter(
        (leg) => !legIds.includes(leg.leg_id),
      );
      updatePosition({ option_legs: updatedLegs ?? [] });
    },
    [updatePosition, position.option_legs],
  );

  const updateIVSettings = useCallback(
    (ivSettings: Partial<IVAdjustmentSettings>) => {
      const updatedIVSettings = [...(position.iv_adjustments ?? [])];
      const expiry = ivSettings.expiry ?? 0;

      const existingIndex = updatedIVSettings.findIndex(
        (iv) => iv.expiry === expiry,
      );

      // Check if the new settings match defaults
      const isDefault =
        ivSettings.level_shift === OC_DEFAULT_LEVEL_SHIFT &&
        ivSettings.call_slope_mult === OC_DEFAULT_CALL_SLOPE_MULT &&
        ivSettings.put_slope_mult === OC_DEFAULT_PUT_SLOPE_MULT;

      if (isDefault && existingIndex !== -1) {
        // Remove from array if it exists and matches defaults
        updatedIVSettings.splice(existingIndex, 1);
      } else {
        // Add or update non-default settings
        const insertIdx =
          existingIndex >= 0 ? existingIndex : updatedIVSettings.length;
        updatedIVSettings[insertIdx] = {
          ...(updatedIVSettings[existingIndex] ?? {}),
          ...ivSettings,
          expiry,
        } as IVAdjustmentSettings;
      }

      updatePosition({ iv_adjustments: updatedIVSettings });
    },
    [updatePosition, position.iv_adjustments],
  );

  // Handle position name changes
  const handleNameChange = useCallback(
    (newName: string) => {
      setPositionName(newName);

      if (isSavedPosition) {
        // For saved positions, use debounced update
        debouncedUpdatePositionName(newName);
      } else {
        // For new positions, update immediately
        updatePosition({ name: newName });
      }
    },
    [isSavedPosition, debouncedUpdatePositionName, updatePosition],
  );

  const handleNameBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.stopPropagation();
      setEditingName(false);

      // If there's a pending debounced update for saved positions, execute it immediately
      if (isSavedPosition && nameUpdateTimeoutRef.current) {
        clearTimeout(nameUpdateTimeoutRef.current);
        updatePosition({ name: pendingNameRef.current });
        nameUpdateTimeoutRef.current = null;
      }
    },
    [isSavedPosition, updatePosition],
  );

  // Memoize the grouped legs
  const groupedLegs = useMemo(() => {
    return position.option_legs?.reduce(
      (groups, leg) => {
        const expiry = leg.expiry;
        if (!groups[expiry]) {
          groups[expiry] = [];
        }
        groups[expiry].push(leg);
        return groups;
      },
      {} as Record<number, PositionLeg[]>,
    );
  }, [position.option_legs]);

  // Calculate expirations from greeks data
  const expirationOptions = useMemo(() => {
    const greeksExpirations = greeks
      ? Object.keys(greeks).map((key) => Number(key))
      : [];
    return [...new Set([...greeksExpirations])].sort((a, b) => a - b);
  }, [greeks]);

  // Check for expired legs
  const expiredLegsInfo = useMemo(() => {
    const now = dayjs();
    const expiredLegs =
      position.option_legs?.filter((leg) => {
        const expiryDate = dayjs(leg.expiry);
        return expiryDate.isBefore(now, 'day');
      }) || [];

    return {
      hasExpiredLegs: expiredLegs.length > 0,
      expiredLegs,
      expiredCount: expiredLegs.length,
    };
  }, [position.option_legs]);

  // Helper functions for formatting descriptions
  const formatOptionLegDescription = useCallback(
    (leg: PositionLeg) => {
      const action = leg.quantity > 0 ? 'BTO' : 'STO';
      const optionType = leg.option_type;
      const expiryDate = dayjs(leg.expiry).format('YYYY-MM-DD');
      const strike = leg.strike?.toFixed(0);
      const price = leg.price_paid?.toFixed(2);
      const quantity = Math.abs(leg.quantity);
      return `${action} ${quantity}x ${sym} ${strike}${optionType} ${expiryDate} @ $${price}`;
    },
    [sym],
  );

  const formatSharesDescription = useCallback(
    (shares: PositionShares) => {
      const action = shares.quantity > 0 ? 'Long' : 'Short';
      const quantity = Math.abs(shares.quantity);
      const price = shares.price_paid?.toFixed(2);
      return `${action} ${quantity} shares of ${sym} @ $${price}`;
    },
    [sym],
  );

  const formatIVAdjustmentDescription = useCallback(
    (expiry: number, settings: IVAdjustmentSettings) => {
      const expiryDate = dayjs(expiry).format('YYYY-MM-DD');
      const levelShiftSign = settings.level_shift >= 0 ? '+' : '';
      return `${expiryDate}: IV shift ${levelShiftSign}${formatAsPercentage(
        settings.level_shift,
      )}, Call Skew ${settings.call_slope_mult.toFixed(2)}x, Put Skew ${settings.put_slope_mult.toFixed(2)}x`;
    },
    [],
  );

  // Calculate position summary
  const positionSummary = useMemo(() => {
    const totalLegs = position.option_legs.length;
    const totalShares = position.underlying_shares.length;
    const totalIVAdjustments = position.iv_adjustments.length;

    const totalPremium = position.option_legs.reduce((sum, leg) => {
      return sum + leg.price_paid * leg.quantity * 100;
    }, 0);

    const totalSharesCost = position.underlying_shares.reduce((sum, shares) => {
      return sum + shares.price_paid * shares.quantity;
    }, 0);

    const netCost = totalPremium + totalSharesCost;

    return {
      totalLegs,
      totalShares,
      totalIVAdjustments,
      totalPremium,
      totalSharesCost,
      netCost,
    };
  }, [position]);

  const handleSavePosition = useCallback(async () => {
    setIsActionLoading(true);
    if (position.id !== NEW_POSITION_ID) {
      openToast({
        message: 'Cannot save new position',
        type: 'error',
      });
      return;
    }

    try {
      await savePosition?.(position, sym, positionName);
      setSaveDialogOpen(false);
      openToast({
        message: 'Position saved successfully',
        type: 'success',
      });
    } catch (error) {
      openToast({
        message: 'Failed to save position',
        type: 'error',
      });
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  }, [position, savePosition, sym, positionName, openToast]);

  const handleEditSavedPosition = useCallback(async () => {
    setIsActionLoading(true);
    try {
      // Ensure any pending name updates are applied before saving
      if (nameUpdateTimeoutRef.current) {
        clearTimeout(nameUpdateTimeoutRef.current);
        updatePosition({ name: pendingNameRef.current });
        nameUpdateTimeoutRef.current = null;
      }

      await editSavedPosition?.(
        originalPosition
          ? {
              ...position,
              deletes: getPositionDeletes(position, originalPosition),
            }
          : position,
        sym,
      );
      setEditDialogOpen(false);
      openToast({
        message: 'Position edited successfully',
        type: 'success',
      });
    } catch (error) {
      openToast({
        message: 'Failed to edit position',
        type: 'error',
      });
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  }, [
    position,
    originalPosition,
    editSavedPosition,
    sym,
    openToast,
    updatePosition,
  ]);

  const handleDeleteSavedPosition = useCallback(async () => {
    setIsActionLoading(true);
    try {
      await deleteSavedPosition?.(position.id);
      setDeleteDialogOpen(false);
      openToast({
        message: 'Position deleted successfully',
        type: 'success',
      });
    } catch (error) {
      openToast({
        message: 'Failed to delete position',
        type: 'error',
      });
      console.error(error);
    } finally {
      setIsActionLoading(false);
    }
  }, [deleteSavedPosition, openToast, position]);

  const handleReverseAllLegs = useCallback(() => {
    const newLegs = position.option_legs.map((leg) => ({
      ...leg,
      quantity: 0 - leg.quantity,
    }));
    updatePosition({ option_legs: newLegs });
    setForceDefaultZoom(true);
  }, [position, setForceDefaultZoom, updatePosition]);

  // Detect changes for saved positions
  const currentPositionChanges = useMemo(() => {
    if (positionId === NEW_POSITION_ID || !originalPosition) {
      return null;
    }

    const changes: string[] = [];

    // Check name changes
    if (originalPosition.name !== position.name) {
      changes.push(
        `Name: "${originalPosition.name || 'Untitled'}" → "${position.name || 'Untitled'}"`,
      );
    }

    // Check option legs changes
    const originalLegs = originalPosition.option_legs || [];
    const currentLegs = position.option_legs || [];

    // Check for removed legs
    originalLegs.forEach((originalLeg, index) => {
      const matchingCurrentLeg = currentLegs.find(
        (leg) => leg.leg_id === originalLeg.leg_id,
      );
      if (!matchingCurrentLeg) {
        changes.push(
          `Removed leg ${index + 1}: ${formatOptionLegDescription(originalLeg)}`,
        );
      }
    });

    // Check for added legs
    currentLegs.forEach((currentLeg, index) => {
      const matchingOriginalLeg = originalLegs.find(
        (leg) => leg.leg_id === currentLeg.leg_id,
      );
      if (!matchingOriginalLeg) {
        changes.push(
          `Added leg ${index + 1}: ${formatOptionLegDescription(currentLeg)}`,
        );
      }
    });

    // Check for modified legs
    currentLegs.forEach((currentLeg, index) => {
      const originalLeg = originalLegs.find(
        (leg) => leg.leg_id === currentLeg.leg_id,
      );
      if (originalLeg) {
        const legChanges: string[] = [];

        if (originalLeg.strike !== currentLeg.strike) {
          legChanges.push(
            `strike: $${originalLeg.strike} → $${currentLeg.strike}`,
          );
        }
        if (originalLeg.expiry !== currentLeg.expiry) {
          legChanges.push(
            `expiry: ${dayjs(originalLeg.expiry).format('YYYY-MM-DD')} → ${dayjs(currentLeg.expiry).format('YYYY-MM-DD')}`,
          );
        }
        if (originalLeg.option_type !== currentLeg.option_type) {
          legChanges.push(
            `type: ${originalLeg.option_type} → ${currentLeg.option_type}`,
          );
        }
        if (originalLeg.quantity !== currentLeg.quantity) {
          legChanges.push(
            `quantity: ${originalLeg.quantity} → ${currentLeg.quantity}`,
          );
        }
        if (originalLeg.price_paid !== currentLeg.price_paid) {
          legChanges.push(
            `price: $${originalLeg.price_paid?.toFixed(2)} → $${currentLeg.price_paid?.toFixed(2)}`,
          );
        }

        if (legChanges.length > 0) {
          changes.push(`Modified leg ${index + 1}: ${legChanges.join(', ')}`);
        }
      }
    });

    // Check underlying shares changes
    const originalShares = originalPosition.underlying_shares || [];
    const currentShares = position.underlying_shares || [];

    // Check for removed shares
    originalShares.forEach((originalShare, index) => {
      const matchingCurrentShare = currentShares.find(
        (_share, i) => i === index, // Simple index-based matching for shares
      );
      if (!matchingCurrentShare) {
        changes.push(
          `Removed shares position: ${formatSharesDescription(originalShare)}`,
        );
      }
    });

    // Check for added shares
    currentShares.forEach((currentShare, index) => {
      const matchingOriginalShare = originalShares.find(
        (_share, i) => i === index,
      );
      if (!matchingOriginalShare) {
        changes.push(
          `Added shares position: ${formatSharesDescription(currentShare)}`,
        );
      }
    });

    // Check for modified shares
    currentShares.forEach((currentShare, index) => {
      const originalShare = originalShares[index];
      if (originalShare) {
        const shareChanges: string[] = [];

        if (originalShare.quantity !== currentShare.quantity) {
          shareChanges.push(
            `quantity: ${originalShare.quantity} → ${currentShare.quantity}`,
          );
        }
        if (originalShare.price_paid !== currentShare.price_paid) {
          shareChanges.push(
            `price: $${originalShare.price_paid?.toFixed(2)} → $${currentShare.price_paid?.toFixed(2)}`,
          );
        }

        if (shareChanges.length > 0) {
          changes.push(`Modified shares position: ${shareChanges.join(', ')}`);
        }
      }
    });

    // Check IV adjustments changes
    const originalIVs = originalPosition.iv_adjustments || [];
    const currentIVs = position.iv_adjustments || [];

    // Check for removed IV adjustments
    originalIVs.forEach((originalIV) => {
      const matchingCurrentIV = currentIVs.find(
        (iv) => iv.expiry === originalIV.expiry,
      );
      if (!matchingCurrentIV) {
        changes.push(
          `Removed IV adjustment for ${dayjs(originalIV.expiry).format('YYYY-MM-DD')}`,
        );
      }
    });

    // Check for added IV adjustments
    currentIVs.forEach((currentIV) => {
      const matchingOriginalIV = originalIVs.find(
        (iv) => iv.expiry === currentIV.expiry,
      );
      if (!matchingOriginalIV) {
        changes.push(
          `Added IV adjustment for ${dayjs(currentIV.expiry).format('YYYY-MM-DD')}`,
        );
      }
    });

    // Check for modified IV adjustments
    currentIVs.forEach((currentIV) => {
      const originalIV = originalIVs.find(
        (iv) => iv.expiry === currentIV.expiry,
      );
      if (originalIV) {
        const ivChanges: string[] = [];

        if (originalIV.level_shift !== currentIV.level_shift) {
          ivChanges.push(
            `level shift: ${originalIV.level_shift} → ${currentIV.level_shift}`,
          );
        }
        if (originalIV.call_slope_mult !== currentIV.call_slope_mult) {
          ivChanges.push(
            `call slope: ${originalIV.call_slope_mult} → ${currentIV.call_slope_mult}`,
          );
        }
        if (originalIV.put_slope_mult !== currentIV.put_slope_mult) {
          ivChanges.push(
            `put slope: ${originalIV.put_slope_mult} → ${currentIV.put_slope_mult}`,
          );
        }

        if (ivChanges.length > 0) {
          changes.push(
            `Modified IV adjustment for ${dayjs(currentIV.expiry).format('YYYY-MM-DD')}: ${ivChanges.join(', ')}`,
          );
        }
      }
    });

    return changes.length > 0 ? changes : null;
  }, [
    originalPosition,
    position,
    positionId,
    formatOptionLegDescription,
    formatSharesDescription,
  ]);

  const getIVSettings = useCallback(
    (expiration: number) => {
      return (
        position.iv_adjustments.find(
          (settings: IVAdjustmentSettings) => settings.expiry === expiration,
        ) ?? {
          position_id: positionId,
          sym,
          expiry: expiration,
          level_shift: OC_DEFAULT_LEVEL_SHIFT,
          call_slope_mult: OC_DEFAULT_CALL_SLOPE_MULT,
          put_slope_mult: OC_DEFAULT_PUT_SLOPE_MULT,
        }
      );
    },
    [position.iv_adjustments, positionId, sym],
  );

  const hasDuplicateLegs = useMemo(() => {
    const legKeys = new Set();
    const duplicateLegs = [];

    position.option_legs?.forEach((leg) => {
      const key = `${position.id}-${sym}-${leg.strike}-${leg.expiry}-${leg.option_type}`;
      if (legKeys.has(key)) {
        duplicateLegs.push(leg);
      } else {
        legKeys.add(key);
      }
    });

    return duplicateLegs.length > 0;
  }, [position.option_legs, sym, position.id]);

  const handleActivatePosition = useCallback(() => {
    if (expiredLegsInfo.hasExpiredLegs) {
      openToast({
        message:
          'Cannot activate position with expired option legs. Please update or remove expired legs first.',
        type: 'error',
      });
      return;
    }
    onSetActivePositionId(positionId);
  }, [
    expiredLegsInfo.hasExpiredLegs,
    onSetActivePositionId,
    positionId,
    openToast,
  ]);

  const renderExpiredLegsWarning = () => {
    if (!expiredLegsInfo.hasExpiredLegs) {
      return null;
    }

    return (
      <>
        <Box
          sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.warning.main, 0.05),
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WarningIcon
              sx={{
                color: theme.palette.warning.main,
                fontSize: 18,
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.warning.main,
                fontSize: { xs: 12, md: 14 },
              }}
            >
              Expired Option Legs Detected
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            This position contains {expiredLegsInfo.expiredCount} expired option
            leg{expiredLegsInfo.expiredCount !== 1 ? 's' : ''}. You cannot
            activate this position until you update or remove the expired legs.
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            {expiredLegsInfo.expiredLegs.map((leg) => (
              <Typography
                key={leg.leg_id}
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '11px',
                  fontFamily: 'monospace',
                }}
              >
                • {formatOptionLegDescription(leg)} (expired{' '}
                {dayjs(leg.expiry).format('MMM D, YYYY')})
              </Typography>
            ))}
          </Stack>
        </Box>
        <Divider
          sx={{
            my: '16px',
            borderColor: alpha(theme.palette.primary.main, 0.25),
          }}
        />
      </>
    );
  };

  const renderActions = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        flexDirection: { xs: 'column', md: 'row' },
      }}
    >
      {/* Position Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <SGTooltip title="Render the PnL chart for this specific position">
          <Button
            variant={isActive ? 'contained' : 'outlined'}
            size="small"
            startIcon={isActive ? <CheckCircleIcon /> : <PlayArrowIcon />}
            onClick={(e) => {
              e.stopPropagation();
              if (!isActive) {
                handleActivatePosition();
                setForceDefaultZoom(true);
              }
            }}
            disabled={isActive || expiredLegsInfo.hasExpiredLegs}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              px: 3,
              py: 0.5,
              position: 'relative',
              '&.Mui-disabled': {
                backgroundColor: expiredLegsInfo.hasExpiredLegs
                  ? alpha(theme.palette.warning.main, 0.1)
                  : alpha(theme.palette.primary.main, 0.1),
                borderColor: expiredLegsInfo.hasExpiredLegs
                  ? alpha(theme.palette.warning.main, 0.5)
                  : alpha(theme.palette.primary.main, 0.5),
                opacity: 0.8,
              },
            }}
          >
            {isActive ? 'Displayed' : 'Display PnL'}
            {expiredLegsInfo.hasExpiredLegs && (
              <WarningIcon
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 12,
                  height: 12,
                  color: theme.palette.warning.main,
                }}
              />
            )}
          </Button>
        </SGTooltip>

        {savePosition && userIsLoggedIn && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setSaveDialogOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              px: 3,
              py: 0.5,
              '&.Mui-disabled': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: alpha(theme.palette.primary.main, 0.25),
                opacity: 0.8,
              },
            }}
          >
            Save Position
          </Button>
        )}
        {editSavedPosition && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<SaveIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setEditDialogOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              px: 3,
              py: 0.5,
              position: 'relative',
              '&.Mui-disabled': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderColor: alpha(theme.palette.primary.main, 0.25),
                opacity: 0.8,
              },
            }}
            disabled={!hasUnsavedChanges}
          >
            Save Changes
            {hasUnsavedChanges && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.error.main,
                }}
              />
            )}
          </Button>
        )}
        {deleteSavedPosition && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              px: 3,
              py: 0.5,
              color: theme.palette.error.main,
              borderColor: theme.palette.error.main,
              '&:hover': {
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                borderColor: theme.palette.error.main,
              },
            }}
          >
            Delete Position
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setClearDialogOpen(true);
          }}
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            px: 3,
            py: 0.5,
            color: alpha(theme.palette.warning.main, 0.8),
            borderColor: alpha(theme.palette.warning.main, 0.8),
            '&:hover': {
              backgroundColor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
              borderColor: theme.palette.warning.main,
            },
          }}
        >
          Clear Position
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={(_e) => handleReverseAllLegs()}
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            px: 3,
            py: 0.5,
            color: alpha(theme.palette.core.price, 0.8),
            borderColor: alpha(theme.palette.core.price, 0.8),
            '&:hover': {
              backgroundColor: alpha(theme.palette.core.price, 0.1),
              color: theme.palette.core.price,
              borderColor: theme.palette.core.price,
            },
          }}
        >
          <Typography color="inherit" sx={{ paddingRight: '8px' }}>
            ⇄
          </Typography>
          Reverse Legs
        </Button>
      </Box>
    </Box>
  );

  const renderOptionsContent = () => (
    <>
      {position.option_legs == null || position.option_legs?.length === 0 ? (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 2 }}
        >
          <Typography color="text.secondary" variant="body2">
            No option positions added yet
          </Typography>
          <Stack direction="row" spacing={2}>
            <SGTooltip title="Add an at-the-money call to the PnL chart, defaulting to the nearest expiration within the position">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddCallLeg}
                sx={{ textTransform: 'none', mr: 1 }}
              >
                Add Call
              </Button>
            </SGTooltip>
            <SGTooltip title="Add an at-the-money put to the PnL chart, defaulting to the nearest expiration within the position">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddPutLeg}
                sx={{ textTransform: 'none' }}
              >
                Add Put
              </Button>
            </SGTooltip>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={4}>
          {Object.entries(groupedLegs ?? {})
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([expirationStr, positionLegs]) => {
              const expiration = Number(expirationStr);
              return (
                <GroupedOptionLegs
                  key={`${position.id}-${sym}-${expiration}`}
                  legs={positionLegs}
                  legIVs={legIVs}
                  adjustedLegIVs={adjustedLegIVs}
                  greeks={greeks}
                  greeksLoading={greeksLoading}
                  stats={greekStats}
                  expiration={expiration}
                  expirationOptions={expirationOptions}
                  strikeDeltaOptions={
                    strikeDataPerExpiration.get(expiration) ?? []
                  }
                  onAddLeg={handleAddLeg}
                  onEditLeg={handleUpdateLeg}
                  onDeleteLeg={removeLeg}
                  onDeleteLegsBulk={removeLegsBulk}
                  onGroupExpirationChange={handleGroupExpirationChange}
                  ivSettings={getIVSettings(expiration)}
                  onUpdateIVSettings={updateIVSettings}
                  currentPrice={currentPrice}
                  sym={sym}
                />
              );
            })}
          {/* Add More Legs Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              pt: 2,
            }}
          >
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddCallLeg}
              sx={{ textTransform: 'none' }}
            >
              Add Call
            </Button>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddPutLeg}
              sx={{ textTransform: 'none' }}
            >
              Add Put
            </Button>
          </Box>
        </Stack>
      )}
    </>
  );

  const renderUnderlyingContent = () => (
    <>
      {position.underlying_shares == null ||
      position.underlying_shares?.length === 0 ? (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ py: 2 }}
        >
          <Typography color="text.secondary" variant="body2">
            No underlying positions added yet
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddShares}
            sx={{ textTransform: 'none' }}
          >
            Add Underlying Position
          </Button>
        </Stack>
      ) : (
        <Stack
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            borderRadius: '8px',
            p: 1,
          }}
        >
          {position.underlying_shares?.map((share, index) => (
            <EditableUnderlyingPosition
              key={index}
              shares={share}
              onEditShares={(updatedFields) =>
                handleEditShares(index, updatedFields)
              }
              onDeleteShares={() => handleDeleteShares(index)}
            />
          ))}
        </Stack>
      )}
    </>
  );

  const renderIdenticalLegsWarning = () => {
    return (
      <>
        <Box
          sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.05),
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: theme.palette.error.main,
              fontSize: { xs: 12, md: 14 },
            }}
          >
            ⚠️ Warning: Identical Legs Detected
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              mb: 1,
            }}
          >
            You cannot save a position with identical option legs. Please
            consolidate or remove duplicate legs with the same strike, expiry,
            and option type.
          </Typography>
        </Box>
        <Divider
          sx={{
            my: '16px',
            borderColor: alpha(theme.palette.primary.main, 0.25),
          }}
        />
      </>
    );
  };

  // Render position name (editable for saved positions)
  const renderPositionName = () => {
    if (isSavedPosition) {
      return (
        <InputBase
          value={positionName}
          onChange={(e) => {
            e.stopPropagation();
            handleNameChange(e.target.value);
          }}
          onBlur={handleNameBlur}
          onFocus={(e) => {
            e.stopPropagation();
            setEditingName(true);
          }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          placeholder={`${sym} Position`}
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: theme.palette.text.primary,
            backgroundColor: editingName
              ? alpha(theme.palette.primary.main, 0.05)
              : alpha(theme.palette.primary.main, 0.02),
            borderRadius: 1,
            px: 1,
            py: 0.25,
            border: editingName
              ? `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              : `1px dashed ${alpha(theme.palette.primary.main, 0.2)}`,
            transition: 'all 0.2s ease-in-out',
            cursor: 'text',
            width: 'fit-content',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
            },
            '&.Mui-focused': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            },
            '& input': {
              padding: 0,
              cursor: 'text',
            },
          }}
        />
      );
    }

    return (
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          fontSize: '14px',
        }}
      >
        {position.name || `${sym} Position`}
      </Typography>
    );
  };

  // Render collapsed summary
  const renderCollapsedSummary = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        gap: { xs: 2, md: 0 },
      }}
    >
      <Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {renderPositionName()}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '11px',
              }}
            >
              {position.option_legs?.length || 0} legs •{' '}
              {position.underlying_shares?.length || 0} shares
            </Typography>
            <Box
              sx={{
                px: 2,
                py: 0.25,
                borderRadius: 0.75,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  fontSize: '9px',
                }}
              >
                {sym}
              </Typography>
            </Box>
            {isActive && (
              <Box
                sx={{
                  px: 2,
                  py: 0.25,
                  borderRadius: 0.75,
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.success.main,
                    fontWeight: 600,
                    fontSize: '9px',
                  }}
                >
                  ACTIVE
                </Typography>
              </Box>
            )}
            {hasUnsavedChanges && (
              <Box
                sx={{
                  px: 2,
                  py: 0.25,
                  borderRadius: 0.75,
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.warning.main,
                    fontWeight: 600,
                    fontSize: '9px',
                  }}
                >
                  UNSAVED
                </Typography>
              </Box>
            )}
            {expiredLegsInfo.hasExpiredLegs && (
              <Box
                sx={{
                  px: 2,
                  py: 0.25,
                  borderRadius: 0.75,
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                }}
              >
                <WarningIcon
                  sx={{
                    color: theme.palette.error.main,
                    fontSize: '8px',
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    fontWeight: 600,
                    fontSize: '9px',
                  }}
                >
                  EXPIRED
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Timestamp */}
        {position.updated_at && (
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '10px',
              fontStyle: 'italic',
            }}
          >
            Updated {dayjs(position.updated_at).format('MMM D, YYYY h:mm A')}
          </Typography>
        )}
      </Stack>

      {/* Action buttons for collapsed view */}
      {renderActions()}
    </Box>
  );

  const renderPositionContent = () => (
    <>
      {/* Expired Legs Warning */}
      {expiredLegsInfo.hasExpiredLegs && (
        <Box sx={{ px: '24px', pt: '16px' }}>{renderExpiredLegsWarning()}</Box>
      )}

      {/* Options Section */}
      <Box
        sx={{
          py: '8px',
          px: '24px',
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          backgroundColor: isActive
            ? alpha(theme.palette.primary.main, 0.05)
            : alpha(theme.palette.primary.main, 0.02),
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: theme.palette.primary.main,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Stack
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {/* Options Heading - Left Side */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              Options
            </Typography>
            {!isCollapsible && (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 0.25,
                    borderRadius: 0.75,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      fontSize: '9px',
                    }}
                  >
                    {sym}
                  </Typography>
                </Box>
                {isActive && (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.25,
                      borderRadius: 0.75,
                      backgroundColor: alpha(theme.palette.success.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.success.main,
                        fontWeight: 600,
                        fontSize: '9px',
                      }}
                    >
                      ACTIVE
                    </Typography>
                  </Box>
                )}
                {expiredLegsInfo.hasExpiredLegs && (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.25,
                      borderRadius: 0.75,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <WarningIcon
                      sx={{
                        color: theme.palette.error.main,
                        fontSize: '8px',
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.error.main,
                        fontWeight: 600,
                        fontSize: '9px',
                      }}
                    >
                      EXPIRED
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </Stack>

          {/* All Action Buttons - Right Side */}
          {(position.option_legs?.length > 0 ||
            position.underlying_shares?.length > 0) &&
            !isCollapsible &&
            renderActions()}
        </Box>
        {renderOptionsContent()}
      </Box>

      {/* Underlying Section */}
      <Box
        sx={{
          py: '12px',
          px: '24px',
          backgroundColor: isActive
            ? alpha(theme.palette.secondary.main, 0.05)
            : alpha(theme.palette.secondary.main, 0.02),
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            backgroundColor: theme.palette.secondary.main,
          },
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 1,
            fontWeight: 600,
            color: theme.palette.secondary.main,
          }}
        >
          Underlying
        </Typography>
        {renderUnderlyingContent()}
      </Box>
    </>
  );

  const renderActionDialogs = () => (
    <>
      {/* Save Position Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              Save Position
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review your position details before saving
            </Typography>
          </Box>
          <IconButton
            onClick={() => setSaveDialogOpen(false)}
            sx={{
              color: `${theme.palette.primary.main} !important`,
            }}
          >
            <CloseIcon
              sx={{ color: `${theme.palette.text.primary} !important` }}
            />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* Warnings Section */}
          <Stack sx={{ gap: 3, mt: 4 }}>
            {expiredLegsInfo.hasExpiredLegs && renderExpiredLegsWarning()}
            {hasDuplicateLegs && renderIdenticalLegsWarning()}

            {/* Position Summary Container */}
            <Box
              sx={{
                px: 4,
                py: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
              }}
            >
              {/* Position Summary */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: theme.palette.primary.main }}
                >
                  {`${sym} Position Summary`}
                </Typography>

                {/* Cost Summary */}
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: 600 }}
                  >
                    Cost Breakdown
                  </Typography>
                  <Stack spacing={1}>
                    {positionSummary.totalPremium !== 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Options Premium:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              positionSummary.totalPremium &&
                              positionSummary.totalPremium > 0
                                ? positiveColor
                                : negativeColor,
                          }}
                        >
                          {formatAsCurrency(positionSummary.totalPremium)}
                        </Typography>
                      </Box>
                    )}
                    {positionSummary.totalSharesCost !== 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                          }}
                        >
                          Shares Cost:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              positionSummary.totalSharesCost &&
                              positionSummary.totalSharesCost > 0
                                ? positiveColor
                                : negativeColor,
                          }}
                        >
                          {formatAsCurrency(positionSummary.totalSharesCost)}
                        </Typography>
                      </Box>
                    )}
                    <Divider
                      sx={{
                        borderColor: alpha(theme.palette.primary.main, 0.25),
                      }}
                    />
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between' }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Net Cost:
                      </Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color:
                            positionSummary.netCost &&
                            positionSummary.netCost > 0
                              ? positiveColor
                              : negativeColor,
                        }}
                      >
                        {formatAsCurrency(positionSummary.netCost)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>

              {/* Option Legs Details */}
              {position.option_legs.length > 0 && (
                <Box sx={{ mb: 3, mt: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.primary.main }}
                  >
                    Option Legs ({position.option_legs.length})
                  </Typography>
                  <Stack spacing={1}>
                    {position.option_legs.map((leg) => (
                      <Box
                        key={leg.leg_id}
                        sx={{
                          p: 2,
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05,
                          ),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatOptionLegDescription(leg)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Shares Details */}
              {position.underlying_shares.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.secondary.main }}
                  >
                    Share Positions ({position.underlying_shares.length})
                  </Typography>
                  <Stack spacing={1}>
                    {position.underlying_shares.map((shares, index) => (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          backgroundColor: alpha(
                            theme.palette.secondary.main,
                            0.05,
                          ),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatSharesDescription(shares)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* IV Adjustments Details */}
              {position.iv_adjustments.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, color: theme.palette.warning.main }}
                  >
                    IV Adjustments ({position.iv_adjustments.length})
                  </Typography>
                  <Stack spacing={1}>
                    {position.iv_adjustments.map((settings) => (
                      <Box
                        key={settings.expiry}
                        sx={{
                          p: 2,
                          backgroundColor: alpha(
                            theme.palette.warning.main,
                            0.05,
                          ),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {formatIVAdjustmentDescription(
                            settings.expiry,
                            settings,
                          )}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            {/* Position Name Section */}
            <Box
              sx={{
                px: 4,
                py: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, color: theme.palette.text.primary }}
              >
                Position Name
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter a name for this position"
                variant="outlined"
                inputProps={{ maxLength: 60 }}
                helperText={`${positionName.length}/60 characters`}
                value={positionName}
                onFocus={(_e) => setEditingName(true)}
                onChange={(e) => setPositionName(e.target.value)}
                sx={{
                  width: '100%',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.35),
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSavePosition}
            disabled={
              isActionLoading ||
              hasDuplicateLegs ||
              expiredLegsInfo.hasExpiredLegs
            }
          >
            {isActionLoading ? 'Saving...' : 'Save Position'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Position Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              Save Changes
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review your changes before saving
            </Typography>
          </Box>
          <IconButton onClick={() => setEditDialogOpen(false)} color="primary">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {expiredLegsInfo.hasExpiredLegs && renderExpiredLegsWarning()}
          {hasDuplicateLegs && renderIdenticalLegsWarning()}
          {/* Changes Detected */}
          {currentPositionChanges && currentPositionChanges.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="h6"
                sx={{ mb: 2, color: theme.palette.warning.main }}
              >
                Changes Detected
              </Typography>
              <Stack spacing={2}>
                {currentPositionChanges.map((change, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.warning.main, 0.05),
                      borderRadius: 1,
                      border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: theme.palette.text.primary,
                        letterSpacing: '0.06em',
                      }}
                    >
                      • {change}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSavedPosition}
            disabled={
              isActionLoading ||
              hasDuplicateLegs ||
              expiredLegsInfo.hasExpiredLegs
            }
          >
            {isActionLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Position Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
          }}
        >
          Delete Position
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Are you sure you want to delete this position?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This action cannot be undone.
            </Typography>
          </Stack>

          {/* Position Summary */}
          <Box sx={{ mt: 4 }}>
            <Typography
              variant="h6"
              sx={{ mb: 2, color: theme.palette.error.main }}
            >
              Position to Delete
            </Typography>
            <Box
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.error.main, 0.05),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Name: {position.name || 'Untitled'}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  mb: 1,
                }}
              >
                Symbol: {sym}
              </Typography>

              {/* Option Legs Summary */}
              {position.option_legs.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, mb: 1 }}
                >
                  Option Legs: {position.option_legs.length} leg
                  {position.option_legs.length !== 1 ? 's' : ''}
                </Typography>
              )}

              {/* Underlying Shares Summary */}
              {position.underlying_shares.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary }}
                >
                  Underlying Shares:{' '}
                  {position.underlying_shares.reduce(
                    (total, share) => total + Math.abs(share.quantity),
                    0,
                  )}{' '}
                  shares
                  {position.underlying_shares.length > 1 &&
                    ` (${position.underlying_shares.length} positions)`}
                </Typography>
              )}

              {/* IV Adjustments Summary */}
              {position.iv_adjustments.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, mt: 1 }}
                >
                  IV Adjustments: {position.iv_adjustments.length} expiration
                  {position.iv_adjustments.length !== 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteSavedPosition}
            disabled={isActionLoading}
          >
            {isActionLoading ? 'Deleting...' : 'Delete Position'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Position Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" component="div">
              Clear Position
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review your position details before clearing
            </Typography>
          </Box>
          <IconButton
            onClick={() => setClearDialogOpen(false)}
            sx={{
              color: `${theme.palette.warning.main} !important`,
            }}
          >
            <CloseIcon
              sx={{ color: `${theme.palette.text.primary} !important` }}
            />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 4 }}>
          <Stack sx={{ gap: 2, mt: 4 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to clear this position? This will remove all
              option legs, underlying shares, and IV adjustments.
            </Typography>
            <Stack
              sx={{
                p: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              }}
            >
              {/* Option Legs Summary */}
              {position.option_legs && position.option_legs.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.primary, mb: 1 }}
                >
                  • Option Legs: {position.option_legs.length} leg
                  {position.option_legs.length !== 1 ? 's' : ''}
                </Typography>
              )}

              {/* Underlying Shares Summary */}
              {position.underlying_shares &&
                position.underlying_shares.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary, mb: 1 }}
                  >
                    • Underlying Shares:{' '}
                    {position.underlying_shares.reduce(
                      (total, share) => total + Math.abs(share.quantity),
                      0,
                    )}{' '}
                    shares
                    {position.underlying_shares.length > 1 &&
                      ` (${position.underlying_shares.length} positions)`}
                  </Typography>
                )}

              {/* IV Adjustments Summary */}
              {position.iv_adjustments &&
                position.iv_adjustments.length > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: theme.palette.text.primary }}
                  >
                    • IV Adjustments: {position.iv_adjustments.length}{' '}
                    expiration
                    {position.iv_adjustments.length !== 1 ? 's' : ''}
                  </Typography>
                )}

              {/* Empty position message */}
              {(!position.option_legs || position.option_legs.length === 0) &&
                (!position.underlying_shares ||
                  position.underlying_shares.length === 0) &&
                (!position.iv_adjustments ||
                  position.iv_adjustments.length === 0) && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                    }}
                  >
                    Position is already empty
                  </Typography>
                )}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setClearDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              handleClearPosition();
              setClearDialogOpen(false);
            }}
          >
            Clear Position
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (isCollapsible) {
    return (
      <Box
        id={`position-${positionId}`}
        sx={{
          border: `1px solid ${alpha(theme.palette.primary.main, isActive ? 0.8 : 0.5)}`,
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          boxShadow: isActive
            ? `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            : `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Accordion
          expanded={expanded}
          onChange={(_, isExpanded) => setExpanded(isExpanded)}
          sx={{
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
            '& .MuiAccordionSummary-root': {
              minHeight: 'auto',
              '&.Mui-expanded': {
                minHeight: 'auto',
              },
            },
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
              '&.Mui-expanded': {
                margin: '12px 0',
              },
            },
          }}
          slotProps={{
            transition: {
              unmountOnExit: true,
            },
          }}
        >
          <AccordionSummary
            sx={{
              px: '24px',
              py: 1,
              backgroundColor: isActive
                ? alpha(theme.palette.primary.main, 0.05)
                : alpha(theme.palette.primary.main, 0.02),
              borderBottom: expanded
                ? `1px solid ${alpha(theme.palette.primary.main, 0.15)}`
                : 'none',
            }}
          >
            {renderCollapsedSummary()}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {renderPositionContent()}
          </AccordionDetails>
        </Accordion>
        {renderActionDialogs()}
      </Box>
    );
  }

  // Non-collapsible version (original layout)
  return (
    <Box
      id={`position-${positionId}`}
      sx={{
        border: `1px solid ${alpha(theme.palette.primary.main, isActive ? 0.8 : 0.5)}`,
        borderRadius: 3,
        backgroundColor: theme.palette.background.paper,
        boxShadow: isActive
          ? `0 6px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          : `0 4px 20px ${alpha(theme.palette.common.black, 0.08)}`,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      {/* Timestamp */}
      {position.updated_at && (
        <Box sx={{ px: '24px', py: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '10px',
              fontStyle: 'italic',
            }}
          >
            Updated {dayjs(position.updated_at).format('MMM D, YYYY h:mm A')}
          </Typography>
        </Box>
      )}

      {renderPositionContent()}
      {renderActionDialogs()}
    </Box>
  );
};

export default Position;
